import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Topbar from "../components/Topbar";
import Footer from "../components/Footer";
import { toast } from "react-toastify";
import api from "../utilis/api";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const TrackOrder = () => {
  const [searchParams] = useSearchParams();
  const { user } = useSelector((state) => state.user);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [email, setEmail] = useState("");
  const [orderStatus, setOrderStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const heroRef = useRef(null);
  const contentRef = useRef(null);

  // Check if order ID is provided in URL (from email links)
  useEffect(() => {
    const orderFromUrl = searchParams.get('order');
    if (orderFromUrl) {
      setTrackingNumber(orderFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    gsap.fromTo(
      heroRef.current,
      { opacity: 0, y: 50 },
      { opacity: 1, y: 0, duration: 1, ease: "power3.out" }
    );

    gsap.fromTo(
      contentRef.current.querySelectorAll(".animate-section"),
      { opacity: 0, y: 30 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        stagger: 0.2,
        ease: "power3.out",
        scrollTrigger: {
          trigger: contentRef.current,
          start: "top 80%",
          toggleActions: "play none none none",
        },
      }
    );
  }, []);

  const handleTrackOrder = async (e) => {
    e.preventDefault();
    if (!trackingNumber.trim()) {
      toast.error("Please enter a tracking number or order ID");
      return;
    }
    
    // For guest users, require email
    if (!user && !email.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    
    setLoading(true);
    setError("");
    setOrderStatus(null);
    
    try {
      let response;
      
      if (user) {
        // For logged-in users, use the protected route
        response = await api.get(`/orders/order/${trackingNumber}`);
      } else {
        // For guests, use the guest lookup endpoint
        response = await api.post('/orders/guest-lookup', {
          orderId: trackingNumber,
          email: email
        });
      }
      
      if (response.data?.meta?.success) {
        setOrderStatus(response.data.data);
        toast.success("Order found successfully!");
      } else {
        setError(response.data?.meta?.message || "Order not found");
        toast.error(response.data?.meta?.message || "Order not found");
      }
    } catch (err) {
      console.error('Order tracking error:', err);
      const errorMessage = err?.response?.data?.meta?.message || 
                          err?.response?.data?.message || 
                          "Order not found or invalid information provided";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Topbar />
      <div className="bg-black">
        <Navbar />
      </div>
      
      {/* Hero Section */}
      <div ref={heroRef} className="relative py-20 px-4 text-center text-white bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <div className="text-6xl mb-6">🚚</div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-[#A88006]">
            Track Your Order
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed">
            Enter your tracking number to get real-time updates on your fragrance delivery.
          </p>
        </div>
      </div>

      {/* Content Section */}
      <div ref={contentRef} className="max-w-4xl mx-auto px-4 py-16">
        {/* Tracking Form */}
        <div className="animate-section mb-12">
          <form onSubmit={handleTrackOrder} className="bg-white p-8 rounded-xl border border-[#A88006]/20 shadow-lg">
            <h2 className="text-2xl font-bold text-black mb-6 text-center">Enter Tracking Information</h2>
            
            {!user && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-blue-700 text-sm">
                  🔍 <strong>Guest Order Tracking:</strong> Enter your order ID and the email address used during checkout.
                </p>
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Order ID or Tracking Number
                </label>
                <input
                  type="text"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  placeholder="Enter order ID (e.g., GOD-2024-1234) or tracking number"
                  className="w-full px-4 py-3 rounded-lg bg-white border border-gray-300 text-black placeholder-gray-500 focus:outline-none focus:border-[#A88006]"
                />
              </div>
              
              {!user && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter the email used during checkout"
                    className="w-full px-4 py-3 rounded-lg bg-white border border-gray-300 text-black placeholder-gray-500 focus:outline-none focus:border-[#A88006]"
                  />
                </div>
              )}
              
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#A88006] to-[#9F7800] text-white font-semibold px-8 py-3 rounded-lg hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Tracking...
                  </>
                ) : (
                  <>
                    <span className="text-xl">🔍</span>
                    Track Order
                  </>
                )}
              </button>
            </div>
            
            {error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}
          </form>
        </div>

        {/* Order Status */}
        {orderStatus && (
          <div className="animate-section space-y-8">
            {/* Order Summary */}
            <div className="bg-white p-8 rounded-xl border border-[#A88006]/20 shadow-lg">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-black mb-4">Order Details</h3>
                  <div className="space-y-2 text-gray-700">
                    <p><span className="text-[#A88006] font-semibold">Order ID:</span> {orderStatus.orderId || orderStatus._id}</p>
                    <p><span className="text-[#A88006] font-semibold">Status:</span> <span className="text-green-600 font-semibold">{orderStatus.orderStatus || 'Processing'}</span></p>
                    <p><span className="text-[#A88006] font-semibold">Payment Status:</span> <span className={`font-semibold ${orderStatus.paymentStatus === 'Paid' ? 'text-green-600' : 'text-yellow-600'}`}>{orderStatus.paymentStatus || 'Pending'}</span></p>
                    <p><span className="text-[#A88006] font-semibold">Total Amount:</span> ${orderStatus.totalPrice?.toFixed(2) || '0.00'}</p>
                    <p><span className="text-[#A88006] font-semibold">Order Date:</span> {new Date(orderStatus.createdAt).toLocaleDateString()}</p>
                    {orderStatus.guestEmail && (
                      <p><span className="text-[#A88006] font-semibold">Customer:</span> {orderStatus.guestName || 'Guest'} ({orderStatus.guestEmail})</p>
                    )}
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-black mb-4">Items Ordered</h3>
                  <div className="space-y-2">
                    {orderStatus.orderItems?.map((item, index) => (
                      <div key={index} className="flex justify-between text-gray-700">
                        <span>{item.product?.name || item.product?.title || 'Product'} (x{item.quantity})</span>
                        <span>${(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    )) || (
                      <p className="text-gray-500">No items found</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            {orderStatus.shippingAddress && (
              <div className="bg-white p-8 rounded-xl border border-[#A88006]/20 shadow-lg">
                <h3 className="text-xl font-bold text-black mb-4">Shipping Address</h3>
                <div className="text-gray-700">
                  <p className="font-semibold">{orderStatus.shippingAddress.name}</p>
                  <p>{orderStatus.shippingAddress.address}</p>
                  <p>{orderStatus.shippingAddress.city}, {orderStatus.shippingAddress.state} {orderStatus.shippingAddress.postalCode}</p>
                  <p>{orderStatus.shippingAddress.country}</p>
                  {orderStatus.shippingAddress.phone && (
                    <p className="mt-2">Phone: {orderStatus.shippingAddress.phone}</p>
                  )}
                </div>
              </div>
            )}

            {/* Order Timeline */}
            <div className="bg-white p-8 rounded-xl border border-[#A88006]/20 shadow-lg">
              <h3 className="text-xl font-bold text-black mb-6">Order Timeline</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1">
                    <span className="text-green-500 text-xl">✓</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                      <h4 className="font-semibold text-black">Order Placed</h4>
                      <span className="text-sm text-gray-600">
                        {new Date(orderStatus.createdAt).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
                
                {orderStatus.paymentStatus === 'Paid' && (
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-1">
                      <span className="text-green-500 text-xl">✓</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <h4 className="font-semibold text-black">Payment Confirmed</h4>
                        <span className="text-sm text-gray-600">
                          {orderStatus.paidAt ? new Date(orderStatus.paidAt).toLocaleString() : 'Confirmed'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1">
                    {orderStatus.orderStatus === 'Processing' ? (
                      <span className="text-[#A88006] text-xl">🕰</span>
                    ) : (
                      <span className="text-green-500 text-xl">✓</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                      <h4 className={`font-semibold ${orderStatus.orderStatus === 'Processing' ? 'text-[#A88006]' : 'text-black'}`}>
                        Processing
                      </h4>
                      <span className="text-sm text-gray-600">
                        {orderStatus.orderStatus === 'Processing' ? 'In Progress' : 'Completed'}
                      </span>
                    </div>
                    {orderStatus.orderStatus === 'Processing' && (
                      <p className="text-sm text-[#A88006] mt-1">Your order is currently being processed</p>
                    )}
                  </div>
                </div>
                
                {['Shipped', 'Delivered'].includes(orderStatus.orderStatus) && (
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-1">
                      {orderStatus.orderStatus === 'Shipped' ? (
                        <span className="text-[#A88006] text-xl">🚚</span>
                      ) : (
                        <span className="text-green-500 text-xl">✓</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <h4 className={`font-semibold ${orderStatus.orderStatus === 'Shipped' ? 'text-[#A88006]' : 'text-black'}`}>
                          Shipped
                        </h4>
                        <span className="text-sm text-gray-600">
                          {orderStatus.orderStatus === 'Shipped' ? 'In Transit' : 'Completed'}
                        </span>
                      </div>
                      {orderStatus.orderStatus === 'Shipped' && (
                        <p className="text-sm text-[#A88006] mt-1">Your order is on its way</p>
                      )}
                    </div>
                  </div>
                )}
                
                {orderStatus.orderStatus === 'Delivered' && (
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-1">
                      <span className="text-green-500 text-xl">📦</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <h4 className="font-semibold text-green-600">Delivered</h4>
                        <span className="text-sm text-gray-600">
                          {orderStatus.deliveredAt ? new Date(orderStatus.deliveredAt).toLocaleString() : 'Delivered'}
                        </span>
                      </div>
                      <p className="text-sm text-green-600 mt-1">Your order has been delivered successfully!</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Delivery Information */}
            <div className="bg-gradient-to-r from-[#A88006]/10 to-[#9F7800]/10 p-6 rounded-xl border border-[#A88006]/20">
              <h4 className="text-lg font-semibold text-[#A88006] mb-2">Delivery Information</h4>
              <p className="text-gray-700 text-sm">
                {orderStatus.orderStatus === 'Delivered' 
                  ? 'Your package has been successfully delivered. Thank you for your purchase!'
                  : 'Your package will be delivered to the address provided during checkout. Please ensure someone is available to receive the package or consider delivery to a secure location.'
                }
              </p>
            </div>
          </div>
        )}

        {/* Help Section */}
        <div className="animate-section mt-16 text-center bg-white p-8 rounded-xl border border-[#A88006]/20 shadow-lg">
          <h3 className="text-2xl font-bold text-black mb-4">Need Help?</h3>
          <p className="text-gray-700 mb-6">
            Can't find your tracking information or have questions about your order?
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:info@giftofgodfragrances.com"
              className="bg-gradient-to-r from-[#A88006] to-[#9F7800] text-white font-semibold px-6 py-3 rounded-lg hover:opacity-90 transition-all"
            >
              Contact Support
            </a>
            <a
              href="tel:+18486670850"
              className="border border-[#A88006] text-[#A88006] font-semibold px-6 py-3 rounded-lg hover:bg-[#A88006]/10 transition-all"
            >
              Call (848) 667-0850
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TrackOrder;